import { AxiosResponse } from 'axios';
import {
    createRequestMapQuestionnaireUrl,
    createUserQuestionnairesURL,
    extendSessionUrl,
    getAssignMapRegionsUrl,
    getDrawerUrl,
    getMarkMyPositionUrl,
    getMasterRegionsUrl,
    getPathTraversalUrl,
    getQuestionnaireDataForUserUrl,
    getRegionFiltersUrl,
    getRequestMapQuestionnaireUrl,
    getUnAssignMapRegionsUrl,
    getUserUrl,
    onboardingQuestionnairesUrl,
    whoAmIUrl
} from 'constants/urls';
import { useCallback } from 'react';
import { UsersParams } from 'types/admin';
import { MantaCareUser, OrySession } from '../../context/UserContext/types';
import { LABEL_OPTION_SEPARATOR } from '../../pages/Questionnaire/Questionnaire';
import { CreateUserQuestionnairesBody, Option, Page, Question, QuestionnaireType } from '../../pages/Questionnaire/types';
import { IRequestHandlers, useAxios } from '../useAxios/useAxios';
import {
    GetMasterRegionResponse,
    GetRegionFiltersResponse,
    MarkMyPositionRequestBody,
    PathTraversalRequestBody,
    UpdateUserRequestBody
} from './types';

/**
 * useMantaCareAPI responsible for all the MantaCare apis.
 */
export const useMantaCareAPI = () => {
    const { request, loading, error } = useAxios();

    /**
     * Get manta care user
     */
    const getUser = useCallback(
        async (oryID: string, onError?: (e: unknown) => void) => {
            const response = await request(
                {
                    url: getUserUrl(oryID),
                    method: 'get'
                },
                onError ? { onError } : {}
            );

            if (!response || !response.data) {
                return undefined;
            }
            const { id, name, phoneNumber, hasCompletedOnboardingQuestionnaire, assignedRegions, isAdmin, email, isArchived, createdAt } =
                response.data;
            const mantaCareUser: MantaCareUser = {
                name,
                phoneNumber,
                id,
                hasCompletedOnboardingQuestionnaire,
                assignedRegions,
                isAdmin,
                email,
                createdAt,
                isArchived
            };
            return mantaCareUser;
        },
        [request]
    );

    /**
     * Get manta care user
     */
    const getCurrentUser = useCallback(
        async (session: OrySession, onError?: (e: unknown) => void) => {
            const response = await request(
                {
                    url: whoAmIUrl(),
                    method: 'get',
                    headers: {
                        Authorization: session.jwt
                    }
                },
                onError ? { onError } : {}
            );

            if (!response || !response.data) {
                return undefined;
            }
            const { id, name, phoneNumber, hasCompletedOnboardingQuestionnaire, assignedRegions, isAdmin, email, createdAt } =
                response.data;
            const mantaCareUser: MantaCareUser = {
                name,
                phoneNumber,
                id,
                hasCompletedOnboardingQuestionnaire,
                assignedRegions,
                isAdmin,
                email,
                createdAt
            };
            return mantaCareUser;
        },
        [request]
    );

    /**
     * Update manta care user
     */
    const updateUser = useCallback(
        async (data: UpdateUserRequestBody, oryID: string) => {
            return await request({
                url: getUserUrl(oryID),
                method: 'patch',
                data
            });
        },
        [request]
    );

    const getOnboardingQuestionnaires = useCallback(async () => {
        const response = await request({
            url: onboardingQuestionnairesUrl(),
            method: 'get'
        });
        if (!response || !response.data) {
            return undefined;
        }
        const questionnaire = response.data as QuestionnaireType;
        // In backend two options can have same value.
        // However combination of option value and label should be unique.
        // Keeping option.value = option.label + '#' + option.value to keep UI logic simple.
        const pages: Page[] = questionnaire.pages.map(page => {
            const questions: Question[] = page.questions.map(question => {
                return {
                    ...question,
                    options: question.options?.map(option => {
                        return {
                            ...option,
                            value: option.label + LABEL_OPTION_SEPARATOR + option.value
                        };
                    }) as Option[]
                };
            });
            return {
                ...page,
                questions
            };
        });
        return { ...questionnaire, pages };
    }, [request]);

    const createUserQuestionnaires = useCallback(
        async (userID: string, body: CreateUserQuestionnairesBody) => {
            const response = await request({
                url: createUserQuestionnairesURL(userID),
                method: 'post',
                data: body
            });
            return response;
        },
        [request]
    );

    const getMapQuestionnaires = useCallback(async () => {
        const response = await request({
            url: getRequestMapQuestionnaireUrl(),
            method: 'get'
        });
        if (!response || !response.data) {
            return undefined;
        }
        const questionnaire = response.data as QuestionnaireType;
        // In backend two options can have same value.
        // However combination of option value and label should be unique.
        // Keeping option.value = option.label + '#' + option.value to keep UI logic simple.
        const pages: Page[] = questionnaire.pages.map(page => {
            const questions: Question[] = page.questions.map(question => {
                return {
                    ...question,
                    options: question.options.map(option => {
                        return {
                            ...option,
                            value: option.label + LABEL_OPTION_SEPARATOR + option.value
                        };
                    }) as Option[]
                };
            });
            return {
                ...page,
                questions
            };
        });
        return { ...questionnaire, pages };
    }, [request]);

    const createMapQuestionnaires = useCallback(
        async (userID: string, body: CreateUserQuestionnairesBody) => {
            const response = await request({
                url: createRequestMapQuestionnaireUrl(userID),
                method: 'post',
                data: body
            });
            return response;
        },
        [request]
    );

    /**
     * Get admin users
     */
    const getUsersForAdmin = useCallback(
        async (params: UsersParams) => {
            const response = await request({
                url: `/users`,
                method: 'get',
                params
            });
            if (!response || !response.data) {
                return undefined;
            }
            return response.data;
        },
        [request]
    );

    /**
     * Get questionnaire data of user
     */
    const getQuestionnaireDataForUser = useCallback(
        async id => {
            const response = await request({
                url: getQuestionnaireDataForUserUrl(id),
                method: 'get'
            });
            if (!response || !response.data) {
                return undefined;
            }
            return response.data;
        },
        [request]
    );

    const isSuccessResponse = (response?: AxiosResponse) => {
        if (!response) {
            return false;
        }
        return response.status >= 200 && response.status < 300;
    };

    const pathTraversal = useCallback(
        async (userId: string, regionId: string, data: PathTraversalRequestBody) => {
            return await request({
                url: getPathTraversalUrl(userId, regionId),
                method: 'put',
                data
            });
        },
        [request]
    );

    const markMyPosition = useCallback(
        async (userId: string, regionId: string, data: MarkMyPositionRequestBody) => {
            return await request({
                url: getMarkMyPositionUrl(userId, regionId),
                method: 'patch',
                data
            });
        },
        [request]
    );

    const getDrawer = useCallback(
        async id => {
            const response = await request({
                url: getDrawerUrl(id),
                method: 'get'
            });
            if (!response || !response.data) {
                return undefined;
            }
            return response.data;
        },
        [request]
    );

    const updateUserQuestionnaire = useCallback(
        async (userID, questionnaireID, data) => {
            const response = await request({
                url: `/users/${userID}/questionnaires/${questionnaireID}`,
                method: 'patch',
                data
            });
            if (!response || !response.data) {
                return undefined;
            }
            return response.data;
        },
        [request]
    );

    const getMasterRegions = useCallback(
        async (query?: string, handlers?: IRequestHandlers) => {
            const response = await request(
                {
                    url: getMasterRegionsUrl(query),
                    method: 'get'
                },
                handlers
            );
            if (!response || !response.data) {
                return undefined;
            }
            return response.data as GetMasterRegionResponse;
        },
        [request]
    );

    const getRegionFilters = useCallback(
        async (handlers?: IRequestHandlers) => {
            const response = await request(
                {
                    url: getRegionFiltersUrl(),
                    method: 'get'
                },
                handlers
            );
            if (!response || !response.data) {
                return undefined;
            }
            return response.data as GetRegionFiltersResponse;
        },
        [request]
    );

    const assignRegions = useCallback(
        async (userID: string, regionIDs: string[], handlers?: IRequestHandlers) => {
            return request(
                {
                    url: getAssignMapRegionsUrl(userID),
                    method: 'post',
                    data: {
                        regionIDs
                    }
                },
                handlers
            );
        },
        [request]
    );

    const unAssignRegions = useCallback(
        async (userID: string, regionIDs: string[], handlers?: IRequestHandlers) => {
            return request(
                {
                    url: getUnAssignMapRegionsUrl(userID),
                    method: 'patch',
                    data: {
                        regionIDs
                    }
                },
                handlers
            );
        },
        [request]
    );

    const extendSession = useCallback((userID: string, jwt: string, handlers?: IRequestHandlers) => {
        return request(
            {
                url: extendSessionUrl(userID),
                method: 'post',
                headers: {
                    Authorization: jwt
                }
            },
            handlers
        );
    }, []);

    return {
        getUser,
        getOnboardingQuestionnaires,
        updateUser,
        createUserQuestionnaires,
        isSuccessResponse,
        getUsersForAdmin,
        getQuestionnaireDataForUser,
        pathTraversal,
        markMyPosition,
        getDrawer,
        updateUserQuestionnaire,
        getMasterRegions,
        assignRegions,
        getRegionFilters,
        loading,
        getCurrentUser,
        extendSession,
        error,
        unAssignRegions,
        getMapQuestionnaires,
        createMapQuestionnaires
    };
};
